// sync-ledger.ts (Story 7.6) — the PURE Sync-Ledger model + read-side reconciler.
//
// The ledger is the OBSERVABILITY half of Epic 7: a per-evaluation record of what happened to an
// offline edit once it left the queue. It is built by DERIVING status from the durable IndexedDB
// state the existing sync writers already leave behind — `outbound_queue` (presence + `last_error`),
// `drafts.conflict`, `drafts.state` — plus the prior `cache_metadata` ledger row. It NEVER modifies
// the shared `drain-core`/`sw.ts`/producer (Decision 1): touching the single highest-risk
// file in the offline stack for a read-only feature is not justified.
//
// WHY this module imports only types + PURE helpers from `idb-schema-def` (no `dexie`, no `window`,
// no React): same split as `drain-core` — which is why the `filterOwned` owner predicate lives in
// that pure module rather than in the Dexie-side `owner-scope.ts`.
// The Dexie tables + an ISO clock arrive via dependency injection (`ReconcileDeps`), so the reconciler
// is trivially unit-testable with `fake-indexeddb` and a fixed `now()`, and carries no runtime coupling.
//
// The terminal-state problem (Decision 1): `drain-core` DELETES the `outbound_queue` op on a successful
// drain (AC10) and on a 410 adoption (AC13), so by read time a `Sent`/`Server-Adopted` eval has no
// surviving op — and a synced-offline eval is otherwise indistinguishable from a never-queued online
// one. The reconciler captures those terminal outcomes because the eval was recorded `queued` on an
// EARLIER reconcile (while the op still sat in the queue, deferred offline); when the op later
// disappears AND a prior ledger record exists, the terminal transition is inferred from `drafts.state`.

import { filterOwned } from "./idb-schema-def";
import type { CacheMetaRecord, DraftRecord, OutboundOp } from "./idb-schema-def";

/** The five epic-AC-locked ledger statuses. Internal KEYS are fixed; the clinician-facing pill
 *  WORDING (see `LEDGER_STATUS_META`) has latitude per the QA doc. */
export type LedgerStatus = "queued" | "sent" | "server-adopted" | "conflict" | "rejected";

export interface LedgerEntry {
  eval_id: string;
  /** PHI-FREE: error code/excerpt, synced/adopted version, or attempt count — NEVER a name/score/note. */
  status: LedgerStatus;
  detail: string;
  /** First time this eval was seen needing sync (ISO 8601). Preserved across status transitions. */
  created_at: string;
  /** Last status transition (ISO 8601). Bumped only when the status changes (stable list ordering). */
  updated_at: string;
}

/** The `cache_metadata` key convention provisioned by Story 7.3 for exactly this story
 *  (`idb-schema-def.ts:68`). One row per tracked evaluation. */
export const LEDGER_KEY_PREFIX = "ledger:";

/** Build the `cache_metadata` PK for an evaluation's ledger row. */
export function ledgerKey(evalId: string): string {
  return `${LEDGER_KEY_PREFIX}${evalId}`;
}

// --- Pill label ↔ variant map (AC7) -----------------------------------------------------------
// One place for the status→{label, variant} mapping, mirroring `evaluation-status.ts`'s
// EVALUATION_STATUS_META. Reuses EXISTING Badge variants only (no new variant) — all desaturated /
// clinical, never alarm: queued→info, sent→ok, server-adopted→neutral, conflict→warning,
// rejected→pending.
//
// COPY NOTE — clinician-facing labels (status KEYS stay AC-locked; only the wording has latitude per
// the QA doc, R-E7-7 jargon-leak guard). Decided in the 2026-06-29 code review:
//   • "server-adopted" → "Already submitted" — terminal, informational; the clinician has no action.
//   • "conflict"       → "Needs attention"   — the ONE actionable status (its drawer links to the
//                                               7.5 resolver), so the call-to-action wording fits.
//   • "rejected"       → "Retrying" (pill `pending`, not alarm `blocked`) — `drain-core` (AC14)
//     retains the op and RETRIES every error (5xx / network / 422 / 403) indefinitely, and the HTTP
//     status is not persisted read-side (only the message string), so a queued op carrying a
//     `last_error` is still being retried — never terminally rejected from the client's vantage.
//     Calling it "Rejected" (red) overclaimed failure. A TRUE terminal-Rejected split (4xx vs 5xx)
//     needs `drain-core` to expose the status — deferred (spec Decision 1 "option A" upgrade).

/** The Badge variants the ledger reuses (subset of badge.tsx's variants). */
export type LedgerPillVariant = "info" | "ok" | "neutral" | "warning" | "blocked" | "pending";

export interface LedgerStatusMeta {
  /** Human label shown inside the pill + paired explicit text (never colour-only). */
  label: string;
  /** Badge variant token (clinical, desaturated). */
  pillVariant: LedgerPillVariant;
}

export const LEDGER_STATUS_META: Record<LedgerStatus, LedgerStatusMeta> = {
  queued: { label: "Queued", pillVariant: "info" },
  sent: { label: "Sent", pillVariant: "ok" },
  "server-adopted": { label: "Already submitted", pillVariant: "neutral" },
  conflict: { label: "Needs attention", pillVariant: "warning" },
  rejected: { label: "Retrying", pillVariant: "pending" },
};

// --- Injected dependencies (structural — the real Dexie tables satisfy these) ------------------

export interface ReconcileDeps {
  /** `outbound_queue` reader — the pending create/update ops drain-core has not yet drained. */
  outbound_queue: { toArray(): Promise<OutboundOp[]> };
  /** `drafts` reader — carries `conflict`, `state`, `version` for each evaluation. */
  drafts: { toArray(): Promise<DraftRecord[]> };
  /** `cache_metadata` — the ledger's persistence store; read prior `ledger:*` rows, upsert, and
   *  prune an orphaned row whose draft+op are both gone. */
  cache_metadata: {
    toArray(): Promise<CacheMetaRecord[]>;
    put(rec: CacheMetaRecord): Promise<unknown>;
    delete(key: string): Promise<void>;
  };
  /** ISO-8601 clock, injected so `created_at`/`updated_at` are deterministic under test. */
  now: () => string;
  /**
   * Story 19.6 Unit E — the current device owner (`AuthUser.userId`), already resolved by the hook.
   * The reconcile then considers ONLY that owner's ops, drafts and `ledger:*` rows, and stamps the
   * rows it upserts, so a shared device never shows one clinician the previous clinician's sync
   * history (AC19). A row it does not own is invisible here AND untouched on disk — never pruned.
   * `undefined` (no identity resolvable) yields an EMPTY ledger: fail closed, show nothing.
   *
   * REQUIRED, exactly like `DrainDeps.owner` (19.6 review). It was optional, so deleting the one
   * wiring line in `use-sync-ledger.ts` still type-checked and silently reinstated a cross-clinician
   * ledger with not one test red. There is no legitimate caller that wants an unfiltered ledger, so a
   * missing owner is now a `tsc -b` error rather than a quiet isolation regression; a harness that
   * wants "no identity" passes `undefined` explicitly and gets the fail-closed empty ledger.
   */
  owner: string | undefined;
}

// --- Public entry -----------------------------------------------------------------------------

/**
 * Reconcile the Sync Ledger once: read `outbound_queue` + `drafts` + existing `ledger:*` rows, derive
 * the current status per evaluation (AC4 table), upsert any changed `cache_metadata` rows, and return
 * the full current ledger ordered `updated_at` DESC.
 *
 * Pure: no network, no DB handle of its own — everything arrives via `deps`. An upsert happens only
 * when the status OR detail changed, so a steady reconcile (on every focus/drain) is write-free and
 * the `updated_at` ordering stays stable (it bumps only on a real status transition).
 */
export async function reconcileLedger(deps: ReconcileDeps): Promise<LedgerEntry[]> {
  // Story 19.6 Unit E (19.6 review) — no resolvable device identity ⇒ we do not know whose rows these
  // are, so show NONE of them and write nothing. Explicit rather than leaning on `filterOwned`'s
  // fail-closed `undefined` behaviour: there is no unfiltered path in this function to fall into, and
  // the guard says so at the top where a maintainer reads it (mirrors `drainQueue`'s owner gate).
  //
  // DOCUMENTATION, NOT A TESTED FENCE (19.6 review round 3): deleting this line is behaviourally
  // identical to falling through to `filterOwned`, which already fails closed, so NO test can red on
  // its removal and none claims to. The enforced fence is the REQUIRED `owner` field above — a missing
  // wiring line in `use-sync-ledger.ts` is a `tsc -b` error. CI runs neither `tsc` nor vitest, so that
  // fence is local-only.
  if (!deps.owner) return [];
  const now = deps.now();
  const [allOps, allDrafts, allCacheRows] = await Promise.all([
    deps.outbound_queue.toArray(),
    deps.drafts.toArray(),
    deps.cache_metadata.toArray(),
  ]);
  // Story 19.6 Unit E — scope everything the reconcile can see (and therefore everything it can
  // upsert or prune) to the current device owner, so the Submitted tab never shows one clinician the
  // previous clinician's sync history on a shared device (AC19). Rows belonging to someone else — and
  // UNATTRIBUTED pre-v3 rows, which belong to nobody — are invisible here and untouched on disk.
  const ops = filterOwned(allOps, deps.owner);
  const drafts = filterOwned(allDrafts, deps.owner);
  const cacheRows = filterOwned(allCacheRows, deps.owner);

  // Group ops by eval_id (an eval may carry a create + the latest update both pending).
  const opsByEval = new Map<string, OutboundOp[]>();
  for (const op of ops) {
    const arr = opsByEval.get(op.eval_id);
    if (arr) arr.push(op);
    else opsByEval.set(op.eval_id, [op]);
  }

  const draftById = new Map<string, DraftRecord>();
  for (const d of drafts) draftById.set(d.id, d);

  // Prior ledger rows, keyed by eval_id (filter the `ledger:` prefix out of the shared cache table,
  // which also holds `cache:<resource>` offline-read rows).
  const priorByEval = new Map<string, LedgerEntry>();
  for (const row of cacheRows) {
    if (!row.key.startsWith(LEDGER_KEY_PREFIX)) continue;
    const entry = row.value as LedgerEntry | undefined;
    if (entry && typeof entry === "object" && typeof entry.eval_id === "string") {
      priorByEval.set(entry.eval_id, entry);
    }
  }

  // The evaluations to consider: anything with a pending op (live state) or a prior record (terminal
  // state to re-affirm). An online-only eval (never observed in the queue) is in neither set → never
  // tracked (AC4 last rule, Decision 4).
  const evalIds = new Set<string>([...opsByEval.keys(), ...priorByEval.keys()]);

  const result: LedgerEntry[] = [];
  for (const evalId of evalIds) {
    const evalOps = opsByEval.get(evalId) ?? [];
    const draft = draftById.get(evalId);
    const prior = priorByEval.get(evalId);

    // Prune an ORPHANED ledger row: a prior record whose op AND draft are both gone can no longer be
    // derived to a meaningful state — `deriveStatus` would fall through to `sent` with
    // "Synced as version unknown", an immortal, misleading row (upsert path never deletes). drain-core
    // never deletes a `drafts` row (success/410/409 all mutate-in-place), so a legitimate terminal
    // Sent/Already-submitted row ALWAYS keeps its draft and never hits this guard. Reachability is low
    // (only a partial IDB clear leaves the ledger row without its draft); the guard is cheap and stops
    // the orphan from lingering forever. [Review PR #441 — womanonrails, applied 2026-06-30]
    if (evalOps.length === 0 && !draft && prior) {
      await deps.cache_metadata.delete(ledgerKey(evalId));
      continue;
    }

    const derived = deriveStatus(evalOps, draft, prior !== undefined);
    if (!derived) {
      // No op AND no prior — not tracked. (Unreachable for an evalId drawn from either set above;
      // kept explicit so the contract holds when `deriveStatus` is unit-tested directly.)
      if (prior) result.push(prior);
      continue;
    }

    const created_at = prior?.created_at ?? now;
    // Bump `updated_at` only on a real status transition — a pure detail refresh (e.g. attempt count)
    // keeps the timestamp so the list ordering is stable and the aria-live announcer isn't spammed.
    const updated_at = prior && prior.status === derived.status ? prior.updated_at : now;
    const entry: LedgerEntry = {
      eval_id: evalId,
      status: derived.status,
      detail: derived.detail,
      created_at,
      updated_at,
    };

    const changed = !prior || prior.status !== entry.status || prior.detail !== entry.detail;
    if (changed) {
      // Story 19.6 Unit E — the ledger row carries the owner it describes, so the next clinician on
      // this device cannot read it back (the `filterOwned` above drops it).
      await deps.cache_metadata.put({
        key: ledgerKey(evalId),
        owner: deps.owner,
        value: entry,
        updated_at,
      });
    }
    result.push(entry);
  }

  // Newest transition first. Tie-break deterministically on `created_at` (newer first) then `eval_id`
  // so evals that transitioned in the SAME reconcile (shared `now()`) keep a stable, repeatable order
  // across reconciles rather than falling back to Map/Set iteration order.
  result.sort((a, b) => {
    if (a.updated_at !== b.updated_at) return a.updated_at < b.updated_at ? 1 : -1;
    if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
    return a.eval_id < b.eval_id ? -1 : a.eval_id > b.eval_id ? 1 : 0;
  });
  return result;
}

// --- Derivation (the AC4 transition table) ----------------------------------------------------

/** Derive the ledger status for ONE evaluation from its pending ops + draft + whether a prior ledger
 *  record exists. Returns `null` only when there is NO op AND NO prior record (an untracked, online-only
 *  evaluation). Precedence: conflict > rejected > queued for a live op; terminal sent/server-adopted
 *  only once the op is gone and a prior record proves it was tracked. */
export function deriveStatus(
  ops: OutboundOp[],
  draft: DraftRecord | undefined,
  hasPrior: boolean,
): { status: LedgerStatus; detail: string } | null {
  // A pinned conflict is the meaningful state even though the op is retained alongside it (a 409 keeps
  // the op AND pins `drafts.conflict`). Takes precedence over `queued`.
  const conflict = draft?.conflict ?? null;
  if (conflict) {
    const version = conflict.server_state?.version;
    return {
      status: "conflict",
      detail: `Newer version ${formatVersion(version)} on the server — open to resolve`,
    };
  }

  if (ops.length > 0) {
    // The latest op (by FIFO `queued_at`) carries the live error/attempt state for the eval.
    const sorted = [...ops].sort((a, b) =>
      a.queued_at < b.queued_at ? -1 : a.queued_at > b.queued_at ? 1 : 0,
    );
    const latest = sorted[sorted.length - 1];
    if (latest.last_error != null) {
      // A non-409/410 4xx (or 5xx/network) drain-core recorded via `bumpAttempt`.
      return { status: "rejected", detail: latest.last_error };
    }
    // Sitting in the queue, not yet (successfully) drained — the offline-deferred entry point.
    const detail =
      latest.attempts > 0
        ? `Waiting to sync (${latest.attempts} prior ${latest.attempts === 1 ? "attempt" : "attempts"})`
        : "Waiting to sync";
    return { status: "queued", detail };
  }

  // No op. Only a previously-tracked eval reaches a terminal sync state; an online-only eval (no prior
  // record) stays untracked.
  if (!hasPrior) return null;

  if (draft?.state === "submitted") {
    // 410 silent adoption: the server had already submitted this evaluation (under clinician-only
    // PHI-RLS this is ALWAYS the same clinician on another surface — never another person).
    return {
      status: "server-adopted",
      detail: `Already submitted on the server (version ${formatVersion(draft?.version)}); your offline edit was not applied`,
    };
  }

  // Precondition: drain-core advances `last_synced_version` on every successful drain
  // (`drain-core.ts` success path: `d.last_synced_version = version`), so by the time the op is gone
  // it is the server-acknowledged version. The fall back to the client-local `version` is a defensive
  // last resort for a pre-migration `drafts` row that predates the `last_synced_version` field — it
  // is NOT a routine path. A future maintainer changing that drain invariant must revisit this line.
  const syncedVersion = draft?.last_synced_version ?? draft?.version;
  return { status: "sent", detail: `Synced as version ${formatVersion(syncedVersion)}` };
}

/** Render a version number for a `detail` string; an absent version (corrupt/missing draft) reads as
 *  "unknown" rather than "undefined". */
function formatVersion(version: number | null | undefined): string {
  return typeof version === "number" ? String(version) : "unknown";
}
